<template>
  <lf-card class="mb-8 pt-6">
    <template #header>
      <lf-h3>{{ $t("COMMON.APPLICANT_LINKS") }}</lf-h3>
    </template>
    <div class="flex flex-wrap gap-4" data-cy="owner-tabs">
      <lf-checkmark-tab
        v-for="(owner, index) in owners"
        :key="owner.id"
        class="cursor-pointer"
        :active="selectedOwner?.id === owner.id"
        :show-checkmark="owner.is_primary"
        @click="selectOwner(owner)"
      >
        <div class="flex items-center">
          <icon-base
            :icon="IconOwnerSmall"
            height="12"
            width="10"
            class="mr-2"
          />
          {{ getIndividualName(owner, index) }}
        </div>
        <template #suffix>
          {{ formatPercentage(Number(owner.ownership_percentage)) }}
        </template>
      </lf-checkmark-tab>
    </div>
    <hr class="mt-6" />
    <generate-link
      v-for="(link, index) in personalLinks"
      :key="index"
      :data="link"
      :show-legacy="showLegacy"
      :application-data="applicationData"
      :success-text="successMessage"
      :class="{ 'border-t': index > 0 }"
      :data-cy="link['data-cy']"
    />
  </lf-card>
</template>
<script setup lang="ts">
import { type PropType, computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import { getIndividualName } from "@/helpers/common";
import { APPLICATION_LINK_TYPE_CONTRACT } from "@/helpers/constants";
import { formatPercentage } from "@/helpers/formatting";
import { useAuth } from "@/hooks/auth";
import { useClients } from "@/hooks/clients";
import { useDealOwners } from "@/hooks/deals";

import IconApplicantInfo from "@/components/icons/IconApplicantInfo.vue";
import IconBorrowerPlatform from "@/components/icons/IconBorrowerPlatform.vue";
import IconOwnerSmall from "@/components/icons/IconOwnerSmall.vue";
import IconPersonalInfo from "@/components/icons/IconPersonalInfo.vue";
import LfCheckmarkTab from "@/components/ui/LfCheckmarkTab.vue";
import GenerateLink from "@/views/deals/components/GenerateLink.vue";

import type { IApplication } from "@/models/applications";
import type { IGenerateLinkData } from "@/models/common";
import type { IOwner } from "@/models/users";

const props = defineProps({
  dealOwners: {
    type: Array as PropType<IOwner[]>,
    default: () => [],
  },
  showLegacy: {
    type: Boolean,
    required: false,
    default: false,
  },
  applicationData: {
    type: Object as PropType<IApplication>,
    required: true,
  },
});

const { t } = useI18n();
const applicationOwners = useDealOwners();
const owners = props.dealOwners.length
  ? props.dealOwners
  : applicationOwners.value;
const { authClientSettings } = useClients();
const { isLendflowUser } = useAuth();

const selectedOwner = ref<IOwner | null>(null);

const personalLinks = computed<IGenerateLinkData[]>(() => {
  const options: IGenerateLinkData[] = [
    {
      icon: IconApplicantInfo,
      title: t("DEALS.APPLICATION_LINKS.APPLICANT_CONSENT_LINK"),
      description: t(
        "DEALS.APPLICATION_LINKS.APPLICANT_CONSENT_LINK_DESCRIPTION"
      ),
      action: "applications/generateDocuSignLink",
      linkData: {
        owner: selectedOwner.value?.id,
        application_id: props.applicationData.id,
      },
      type: APPLICATION_LINK_TYPE_CONTRACT,
      showConsentTemplate: true,
      usesModal: true,
    },
    {
      icon: IconApplicantInfo,
      title: t("DEALS.APPLICATION_LINKS.APPLICANT_INFO"),
      description: t("DEALS.APPLICATION_LINKS.APPLICANT_INFO_DESCRIPTION"),
      action: "applications/generateApplicantInfoWithDocuSignLink",
      linkData: {
        owner: selectedOwner.value?.id,
        application_id: props.applicationData.id,
      },
      type: APPLICATION_LINK_TYPE_CONTRACT,
      showConsentTemplate: true,
      usesModal: true,
    },
    {
      icon: IconBorrowerPlatform,
      title: t("DEALS.APPLICATION_LINKS.APPLICANT_CONSENT_AND_BANK_STATEMENTS"),
      description: t(
        "DEALS.APPLICATION_LINKS.APPLICANT_CONSENT_AND_BANK_STATEMENTS_DESCRIPTION"
      ),
      action: "applications/generateApplicantConsentAndBankStatementsLink",
      linkData: {
        ownerId: selectedOwner.value?.id,
        application_id: props.applicationData.id,
      },
      type: APPLICATION_LINK_TYPE_CONTRACT,
      showConsentTemplate: true,
      usesModal: true,
      "data-cy": "consent-bank-statements-link",
    },
    {
      icon: IconApplicantInfo,
      title: t("DEALS.APPLICATION_LINKS.TAX_DETAILS_DOCUSIGN_BANK_STATEMENTS"),
      action: "applications/generateTaxDetailsDocuSignPlaidLink",
      description: t(
        "DEALS.APPLICATION_LINKS.TAX_DETAILS_DOCUSIGN_BANK_STATEMENTS_DESCRIPTION"
      ),
      linkData: {
        owner: selectedOwner.value?.id,
        application_id: props.applicationData.id,
      },
      type: APPLICATION_LINK_TYPE_CONTRACT,
      showConsentTemplate: true,
      usesModal: true,
    },
    {
      icon: IconPersonalInfo,
      title: t("DEALS.APPLICATION_LINKS.PERSONAL_INFO"),
      description: t("DEALS.APPLICATION_LINKS.PERSONAL_INFO_DESCRIPTION"),
      action: "applications/generatePersonalInfoLink",
      linkData: {
        ownerId: selectedOwner.value?.id,
        application_id: props.applicationData.id,
      },
      usesModal: true,
    },
  ];
  if (
    isLendflowUser ||
    authClientSettings.value?.is_redirect_to_borrower_platform_enabled
  ) {
    options.push({
      icon: IconBorrowerPlatform,
      title: t("DEALS.APPLICATION_LINKS.BORROWER_PLATFORM"),
      action: "applications/generateBorrowerPlatformLinkForDeal",
      description: t("DEALS.APPLICATION_LINKS.BORROWER_PLATFORM_DESCRIPTION"),
      linkData: {
        ownerId: selectedOwner.value?.id,
        application_id: props.applicationData.id,
      },
      showExpiration: true,
      showBorrowerPlatformTemplate: true,
      usesModal: true,
      "data-cy": "borrower-platform-link",
    });
  }
  return options;
});

const successMessage = computed(() =>
  t("COMMON.COPIED_LINK_FOR", {
    name: `${selectedOwner.value?.first_name} ${selectedOwner.value?.last_name}`,
  })
);

const selectOwner = (owner: IOwner) => {
  selectedOwner.value = owner;
};

watch(
  owners,
  (updatedOwners) => {
    if (updatedOwners.length) {
      selectedOwner.value = updatedOwners[0];
    }
  },
  { immediate: true }
);
</script>
